/**
 * EmailLayoutManager — manages the 3-pane layout outside of React.
 *
 * Handles:
 *   - ResizeObserver on document.body for mobile breakpoint detection
 *   - Drag-resize of the list pane (mousemove on document)
 *   - CSS custom property --email-list-width sync
 *   - Signal ↔ CSS class sync (sidebar collapsed, mobile panel switching)
 *   - Lazy Turbo Frame eager-loading when the reading pane becomes visible on mobile
 *
 * Works in tandem with the emailStore signals:
 *   sidebarCollapsed, listWidth, isMobileLayout, activePanel
 */

import { effect } from '@preact/signals'

export class EmailLayoutManager {
  constructor({ element, store }) {
    // The layout CSS classes (email-layout--mobile, --show-list, --show-pane) are on the
    // inner .email-layout div rendered by EmailApp, not on the Stimulus controller root.
    this._root = element
    this._el = element.querySelector('.email-layout') || element
    this._store = store
    this._resizing = false
    this._startX = 0
    this._startWidth = 0
    this._disposers = []
    this._observer = null

    this._onMouseMove = this._doResize.bind(this)
    this._onMouseUp   = this._stopResize.bind(this)
  }

  connect() {
    // By the time connect() is called, Preact has synchronously rendered EmailApp
    // into the root element — re-query to make sure we have the inner .email-layout div.
    this._el = this._root.querySelector('.email-layout') || this._root
    this._applyInitialWidth()
    this._setupSignalEffects()
    this._setupResizeObserver()
    this._setupResizerDrag()
    // Sync mobile layout AFTER effects are registered so the activePanel effect fires
    this._store.setLayoutMode(window.innerWidth)
  }

  disconnect() {
    this._disposers.forEach(d => d())
    this._disposers = []
    this._observer?.disconnect()
    this._observer = null
    document.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('mouseup', this._onMouseUp)
  }

  // ── Initial layout restore ──────────────────────────────────────────────

  _applyInitialWidth() {
    // Restore list width CSS custom property, enforcing a minimum of 280px
    const savedWidth = localStorage.getItem('email_list_width')
    if (savedWidth) {
      const clamped = Math.max(280, parseInt(savedWidth, 10))
      const clampedStr = `${clamped}px`
      if (clampedStr !== savedWidth) localStorage.setItem('email_list_width', clampedStr)
      this._el.style.setProperty('--email-list-width', clampedStr)
    }
  }

  // ── Signal → CSS class effects ───────────────────────────────────────────

  _setupSignalEffects() {
    const store = this._store

    // sidebarCollapsed → CSS class
    this._disposers.push(
      effect(() => {
        this._el.classList.toggle(
          'email-layout--sidebar-collapsed',
          store.sidebarCollapsed.value
        )
      })
    )

    // activePanel → CSS classes (mobile + compact-nav stacked nav)
    this._disposers.push(
      effect(() => {
        const panel = store.activePanel.value
        const stacked = store.isMobileLayout.value || store.isCompactNavLayout.value
        if (!stacked) {
          this._el.classList.remove(
            'email-layout--show-sidebar',
            'email-layout--show-list',
            'email-layout--show-pane'
          )
          return
        }
        this._el.classList.toggle('email-layout--show-sidebar', panel === 'sidebar')
        this._el.classList.toggle('email-layout--show-list', panel === 'list')
        this._el.classList.toggle('email-layout--show-pane', panel === 'pane')

        // When pane becomes visible on mobile, trigger lazy Turbo Frames
        if (panel === 'pane' && store.isMobileLayout.value) {
          this._triggerLazyFrames()
          setTimeout(() => this._triggerLazyFrames(), 500)
        }
      })
    )

    // Layout mode CSS classes
    this._disposers.push(
      effect(() => {
        this._el.classList.toggle('email-layout--mobile', store.isMobileLayout.value)
        this._el.classList.toggle('email-layout--compact-nav', store.isCompactNavLayout.value)
      })
    )

    // listWidth → CSS custom property
    this._disposers.push(
      effect(() => {
        const w = store.listWidth.value
        this._el.style.setProperty('--email-list-width', `${w}px`)
      })
    )
  }

  // ── ResizeObserver for mobile breakpoint ─────────────────────────────────

  _setupResizeObserver() {
    if (!window.ResizeObserver) return
    const store = this._store
    this._observer = new ResizeObserver(() => {
      store.setLayoutMode(window.innerWidth)
    })
    this._observer.observe(document.body)
  }

  // ── Drag-resize of list pane ─────────────────────────────────────────────

  _setupResizerDrag() {
    const resizer = this._el.querySelector('[data-email-resizer]')
    if (!resizer) return
    resizer.addEventListener('mousedown', (e) => this._startResize(e))
  }

  _startResize(e) {
    e.preventDefault()
    this._resizing = true
    this._startX = e.clientX

    const listPane = this._el.querySelector('[data-email-list-pane]')
    this._startWidth = listPane
      ? listPane.getBoundingClientRect().width
      : this._store.listWidth.value

    document.addEventListener('mousemove', this._onMouseMove)
    document.addEventListener('mouseup', this._onMouseUp)
  }

  _doResize(e) {
    if (!this._resizing) return
    const delta    = e.clientX - this._startX
    const newWidth = Math.max(280, Math.min(700, this._startWidth + delta))
    this._store.setListWidth(newWidth)
  }

  _stopResize() {
    this._resizing = false
    document.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('mouseup', this._onMouseUp)
  }

  // ── Lazy frame helper (mobile reading pane) ──────────────────────────────

  _triggerLazyFrames() {
    const pane = this._el.querySelector('.email-reading-pane-wrapper')
    if (!pane) return
    pane.querySelectorAll("turbo-frame[loading='lazy'][src]").forEach(frame => {
      frame.setAttribute('loading', 'eager')
    })
  }
}
